import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { toast } from "sonner"
import * as api from "@/entities/bot-builder"
import { draftCache as cache } from "@/entities/bot-builder"
import type { ActionsContext } from "./types"

export function createNodeActions({
	enqueue,
	scenarioId,
	queryClient,
	key,
	halted,
	recovery,
}: ActionsContext) {
	return {
		createNode: (input: api.CreateNodeInput) =>
			enqueue(
				"Не удалось добавить блок",
				(baseRevision) => api.createNode(scenarioId, { ...input, baseRevision }),
				(draft, result) => {
					const next = cache.upsertNode(draft, result.node)
					return result.edge ? cache.upsertEdge(next, result.edge) : next
				},
			),

		updateNode: (
			nodeId: string,
			patch: { config?: Record<string, unknown>; note?: string | null },
		) => {
			const snapshot = { ...((recovery.current[nodeId] as object) ?? {}), ...patch }
			recovery.current[nodeId] = snapshot
			return enqueue(
				"Не удалось сохранить блок",
				(baseRevision) => api.updateNode(scenarioId, nodeId, { ...patch, baseRevision }),
				(draft, result) =>
					cache.removeEdges(
						cache.upsertNode(draft, result.node, { keepPosition: true }),
						result.removedEdgeIds,
					),
			).then((result) => {
				if (result && recovery.current[nodeId] === snapshot) delete recovery.current[nodeId]
				return result
			})
		},

		copyNode: (nodeId: string) =>
			enqueue(
				"Не удалось скопировать блок",
				(baseRevision) => api.copyNode(scenarioId, nodeId, { baseRevision }),
				(draft, result) => cache.upsertNode(draft, result.node),
			),

		moveNode: (nodeId: string, flowId: string) =>
			enqueue(
				"Не удалось перенести блок",
				(baseRevision) => api.moveNode(scenarioId, nodeId, { baseRevision, flowId }),
				(draft, result) =>
					cache.removeEdges(cache.upsertNode(draft, result.node), result.removedEdgeIds),
			),

		deleteNode: (nodeId: string) =>
			enqueue(
				"Не удалось удалить блок",
				(baseRevision) => api.deleteNode(scenarioId, nodeId, baseRevision),
				(draft) => cache.removeNode(draft, nodeId),
			),

		/** Раскладка применяется сразу и сохраняется без ревизии. */
		savePositions: async (positions: api.NodePosition[]) => {
			if (positions.length === 0 || halted.current) return
			queryClient.setQueryData<api.ScenarioDraft>(key, (draft) =>
				draft ? cache.applyPositions(draft, positions) : draft,
			)
			try {
				await api.savePositions(scenarioId, positions)
			} catch (error) {
				toast.error(extractApiErrorMessage(error, "Не удалось сохранить расположение блоков"))
			}
		},
	}
}
